import {useState, useCallback, useRef} from 'react'
import {useQueryClient} from '@tanstack/react-query'
import Modal from '@/customComponents/uiComponents/modal/Modal.jsx'
import ModalHeader from '@/customComponents/uiComponents/modal/ModalHeader.jsx'
import {
    personIcon, planeTakeOff, childSeatIcon, specialRequestIcon, shieldProtected,
    pickupIssueIcon, vehicleIssueIcon, fareChargeIssueIcon, routeNavigationIssueIcon,
    bookingExpectationsIssueIcon, lostItemIssueIcon, otherIssueIcon,
    videoIcon, cameraIcon, fileUploadIcon, arrowDown, greenTick, closeIcon,
} from '@img/index.js'
import {reportService} from '@/services/reportService'

// Issue taxonomy from the design. Categories without a supplied asset still use a DUMMY
// placeholder icon (driver_behavior, airport_pickup, child_seat, special_request,
// safety_concern) — swap when the user provides the rest.
const ISSUE_CATEGORIES = [
    {key: 'driver_behavior', label: 'Driver Behavior', icon: personIcon, issues: ['Unsafe, speeding driving', 'Unprofessional behavior', 'Communication issues', 'Smoking in Vehicle']},
    {key: 'pickup_issues', label: 'Pickup Issues', icon: pickupIssueIcon, issues: ['Driver arrived late', 'Wrong pickup location', 'Left before pickup', 'Could not locate driver']},
    {key: 'vehicle_issues', label: 'Vehicle Issues', icon: vehicleIssueIcon, issues: ['Vehicle not clean, odor', 'Temperature issues (too hot)', 'Vehicle did not match booking']},
    {key: 'fare_charges', label: 'Fare & Charges', icon: fareChargeIssueIcon, issues: ['Incorrect fare', 'Unauthorized charge', 'Toll charge dispute', 'Waiting time dispute', 'Stopover charge', 'Damage charge dispute']},
    {key: 'airport_pickup', label: 'Airport Pickup Issues', icon: planeTakeOff, issues: ['Flight tracking failed', 'Wrong terminal', 'Unclear pickup instructions', 'Could not locate pickup point', 'Luggage assistance']},
    {key: 'child_seat', label: 'Child Seat Issues', icon: childSeatIcon, issues: ['Requested child seat unavailable', 'Incorrect child seat provided', 'Child seat not installed', 'Child seat cleanliness concern']},
    {key: 'special_request', label: 'Special Request Issues', icon: specialRequestIcon, issues: ['Special request not accommodated', 'Oversized luggage issue']},
    {key: 'route_navigation', label: 'Route & Navigation Issues', icon: routeNavigationIssueIcon, issues: ['Wrong route taken, unnecessary detour', 'Excessive route length', 'Driver refused requested route']},
    {key: 'booking_expectations', label: 'Booking Expectations Not Met', icon: bookingExpectationsIssueIcon, issues: ['Wrong route taken, unnecessary detour', 'Missing amenities (child seat, water, etc.)']},
    {key: 'safety_concern', label: 'Safety Concern', icon: shieldProtected, issues: ['Driver appeared impaired', 'Harassment or discrimination concern', 'Unsafe driving, emergency incident']},
    {key: 'lost_item', label: 'Lost Item', icon: lostItemIssueIcon, issues: ['Left item in vehicle', 'Unable to locate item', 'Driver or support unable to help']},
    {key: 'other_issue', label: 'Other Issue', icon: otherIssueIcon, issues: ['Driver appeared impaired', 'Harassment or discrimination concern', 'Issue not listed above']},
]

// Upload tiles — Upload Evidence (Optional) icons. Accepts mirror the API rules
// (images[]: jpeg/png only; video: single mp4/mov/avi) so users can't pick files
// the backend would 422 on.
const UPLOAD_TILES = [
    {kind: 'video', label: 'Video', accept: 'video/mp4,video/quicktime,video/x-msvideo', icon: videoIcon, multiple: false, hint: 'MP4/MOV/AVI . max 50 MB'},
    {kind: 'image', label: 'Photo', accept: 'image/jpeg,image/png', capture: 'environment', icon: cameraIcon, multiple: true, hint: 'JPG/PNG . up to 5 - 10 MB each'},
    // Documents only, so the picker doesn't re-offer images/videos the other two
    // tiles cover. Sent as files[] (separate from images[]) — the API doesn't
    // store documents yet, so they're ignored server-side until it adds support.
    {kind: 'file', label: 'File', accept: '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.zip', icon: fileUploadIcon, multiple: true, hint: 'All type of files . up to 5 - 10 MB each'},
]

// API limits (ratings-feedback-issues-api.md). Images and files count separately.
const MAX_IMAGES = 5
const MAX_FILES = 5
const MAX_IMAGE_MB = 10
const MAX_FILE_MB = 10
const MAX_VIDEO_MB = 50

const issueLabel = (id) => {
    const [key, idxStr] = id.split(':')
    const cat = ISSUE_CATEGORIES.find(c => c.key === key)
    return cat ? cat.issues[Number(idxStr)] : id
}

export default function ReportIssuePopup({isOpen, onClose, rideId}) {
    const queryClient = useQueryClient()
    const [dropdownOpen, setDropdownOpen] = useState(false)
    const [expandedCats, setExpandedCats] = useState(new Set())
    const [selectedIssues, setSelectedIssues] = useState(new Set())
    const [description, setDescription] = useState('')
    const [evidence, setEvidence] = useState([])
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [submitted, setSubmitted] = useState(false)
    const [error, setError] = useState('')
    const fileIdRef = useRef(0)

    const toggleCat = (key) => setExpandedCats(prev => { const n = new Set(prev); n.has(key) ? n.delete(key) : n.add(key); return n })
    const toggleIssue = (id) => setSelectedIssues(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n })

    // Enforce the limits client-side, per kind: up to MAX_IMAGES images and
    // MAX_FILES documents (≤10 MB each), and a single video (≤50 MB) — picking
    // a new video replaces the current one.
    const addFiles = (kind, fileList) => {
        const files = Array.from(fileList || [])
        if (!files.length) return
        let err = ''
        let next = [...evidence]
        for (const file of files) {
            if (kind === 'video') {
                if (file.size > MAX_VIDEO_MB * 1024 * 1024) { err = `Video must be ${MAX_VIDEO_MB} MB or smaller.`; continue }
                next = next.filter(e => e.kind !== 'video')
                next.push({id: ++fileIdRef.current, kind: 'video', file})
            } else if (kind === 'file') {
                if (next.filter(e => e.kind === 'file').length >= MAX_FILES) { err = `You can attach up to ${MAX_FILES} files.`; break }
                if (file.size > MAX_FILE_MB * 1024 * 1024) { err = `Each file must be ${MAX_FILE_MB} MB or smaller.`; continue }
                next.push({id: ++fileIdRef.current, kind: 'file', file})
            } else {
                if (next.filter(e => e.kind === 'image').length >= MAX_IMAGES) { err = `You can attach up to ${MAX_IMAGES} images.`; break }
                if (file.size > MAX_IMAGE_MB * 1024 * 1024) { err = `Each image must be ${MAX_IMAGE_MB} MB or smaller.`; continue }
                next.push({id: ++fileIdRef.current, kind: 'image', file})
            }
        }
        setEvidence(next)
        setError(err)
    }
    const removeFile = (id) => setEvidence(prev => prev.filter(e => e.id !== id))

    const handleClose = useCallback(() => {
        onClose?.()
        setTimeout(() => {
            setDropdownOpen(false)
            setExpandedCats(new Set())
            setSelectedIssues(new Set())
            setDescription('')
            setEvidence([])
            setIsSubmitting(false)
            setSubmitted(false)
            setError('')
        }, 250)
    }, [onClose])

    const handleSubmit = async () => {
        if (selectedIssues.size === 0) return
        setError('')
        setIsSubmitting(true)
        // The API takes a single description string (no category fields) —
        // compose it as one line per category ("Category: issue | issue | ..."),
        // then the free text.
        const issueLines = ISSUE_CATEGORIES
            .map(cat => {
                const picked = cat.issues.filter((_, idx) => selectedIssues.has(`${cat.key}:${idx}`))
                return picked.length ? `${cat.label}: ${picked.join(' | ')}` : null
            })
            .filter(Boolean)
        // API limit: description ≤ 1000 chars SERVER-side. Multipart encodes each
        // \n as \r\n (2 chars), so budget every newline twice when trimming.
        let fullDescription = [...issueLines, description.trim()].filter(Boolean).join('\n')
        const sentLength = (s) => s.length + (s.match(/\n/g) || []).length
        while (sentLength(fullDescription) > 1000) fullDescription = fullDescription.slice(0, -1)
        const result = await reportService.submitReport(rideId, {
            description: fullDescription,
            images: evidence.filter(e => e.kind === 'image').map(e => e.file),
            files: evidence.filter(e => e.kind === 'file').map(e => e.file),
            video: evidence.find(e => e.kind === 'video')?.file || null,
        })
        setIsSubmitting(false)
        if (!result.success) {
            setError(result.error || 'Could not submit your report. Please try again.')
            return
        }
        queryClient.invalidateQueries({queryKey: ['tripIssues', rideId]})
        setSubmitted(true)
    }

    return (
        <Modal isOpen={isOpen} onClose={handleClose}>
            <ModalHeader title="" onClose={handleClose}/>

            {submitted ? (
                <div className="hopn-view-in flex flex-col items-center justify-center py-8 text-center">
                    <img src={greenTick} alt="" className="w-16 h-16 mb-4"/>
                    <h3 className="font-bold mb-3!">Report submitted</h3>
                    <p className="text-(--text-secondary) max-w-100 mb-6!">Thanks for letting us know. Our team will review your report shortly.</p>
                    <button type="button" onClick={handleClose}
                            className="cursor-pointer py-3 px-12 rounded-full bg-(--text-primary) text-white font-bold border-0">
                        Done
                    </button>
                </div>
            ) : (
                <div className="hopn-view-in">
                    {/* Title */}
                    <div className="mb-6">
                        <h3 className="font-bold mb-1!">Report Ride Issue</h3>
                        <p className="text-(--text-secondary) mb-0!">Submit your report. Our team will review it shortly</p>
                    </div>

                    {/* Issue Category */}
                    <div className="mb-6">
                        <h5 className="font-bold mb-3">Issue Category</h5>

                        <button type="button" onClick={() => setDropdownOpen(o => !o)}
                                className="w-full flex items-center justify-between gap-3 border border-(--border-color) rounded-full px-5 py-4 bg-white text-left cursor-pointer">
                            <span className={selectedIssues.size ? 'text-(--text-primary)' : 'text-(--text-secondary)'}>
                                {selectedIssues.size ? `${selectedIssues.size} issue${selectedIssues.size > 1 ? 's' : ''} selected` : 'Select an issue category'}
                            </span>
                            <img src={arrowDown} alt="" className={`w-4 shrink-0 transition-transform duration-200 ${dropdownOpen ? 'rotate-90' : ''}`}/>
                        </button>

                        {dropdownOpen && (
                            <div className="mt-2 rounded-3xl shadow-[0_4px_32px_#00000029] max-h-[320px] overflow-y-auto p-5">
                                {ISSUE_CATEGORIES.map(cat => {
                                    const open = expandedCats.has(cat.key)
                                    return (
                                        <div key={cat.key} className="border-b border-(--border-color) last:border-b-0">
                                            <button type="button" onClick={() => toggleCat(cat.key)}
                                                    className="w-full flex items-center gap-3 px-0 py-4 bg-transparent border-0 cursor-pointer text-left">
                                                <img src={cat.icon} alt="" className="w-6 h-6 shrink-0 object-contain"/>
                                                <span className="font-bold flex-1">{cat.label}</span>
                                                <img src={arrowDown} alt="" className={`w-4 shrink-0 transition-transform duration-200 ${open ? 'rotate-90' : ''}`}/>
                                            </button>
                                            {open && (
                                                <div className="bg-white rounded-2xl shadow-[0_4px_24px_#201e2014] p-4 mb-3 border border-[#CFCDCD] flex flex-col gap-2">
                                                    {cat.issues.map((issue, idx) => {
                                                        const id = `${cat.key}:${idx}`
                                                        const active = selectedIssues.has(id)
                                                        return (
                                                            <button key={id} type="button" onClick={() => toggleIssue(id)}
                                                                    className={`w-full text-left rounded-xl px-4 py-3 cursor-pointer border-0 flex items-center justify-between gap-2 transition-colors ${active ? 'bg-(--bg-grey)' : 'bg-transparent hover:bg-(--bg-grey)'}`}>
                                                                <span>{issue}</span>
                                                                {active && <img src={greenTick} alt="" className="w-4 h-4 shrink-0"/>}
                                                            </button>
                                                        )
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                    )
                                })}
                            </div>
                        )}

                        {selectedIssues.size > 0 && (
                            <div className="flex flex-wrap gap-2 mt-3">
                                {[...selectedIssues].map(id => (
                                    <span key={id} className="inline-flex items-center gap-2 bg-(--bg-grey) rounded-full pl-3 pr-2 py-1 text-sm">
                                        {issueLabel(id)}
                                        <button type="button" onClick={() => toggleIssue(id)} className="cursor-pointer bg-transparent border-0 p-0 flex">
                                            <img src={closeIcon} alt="remove" className="w-3 h-3"/>
                                        </button>
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Describe the issue */}
                    <div className="mb-6">
                        <h5 className="font-bold mb-3">Describe the issue</h5>
                        <div className="relative border border-(--border-color) rounded-2xl">
                            <textarea value={description} onChange={e => setDescription(e.target.value)} rows={4} maxLength={500}
                                      placeholder="Please provide details about the issue..."
                                      className="w-full! rounded-3xl p-4 pb-8 text-base outline-none resize-none"/>
                            <span className="absolute bottom-2 right-3 text-xs text-(--text-secondary)">{description.length}/500</span>
                        </div>
                    </div>

                    {/* Upload Evidence */}
                    <div className="mb-2">
                        <h5 className="font-bold mb-3">Upload Evidence (Optional)</h5>
                        <div className="grid grid-cols-3 gap-3">
                            {UPLOAD_TILES.map(tile => (
                                <label key={tile.label} className="cursor-pointer flex flex-col items-center gap-1.5">
                                    <input type="file" {...(tile.accept ? {accept: tile.accept} : {})} {...(tile.multiple ? {multiple: true} : {})} {...(tile.capture ? {capture: tile.capture} : {})}
                                           onChange={(e) => { addFiles(tile.kind, e.target.files); e.target.value = '' }} className="hidden"/>
                                    <img src={tile.icon} alt={tile.label}/>
                                    <span className="text-[11px] leading-tight text-(--text-secondary) text-center">{tile.hint}</span>
                                </label>
                            ))}
                        </div>

                        {evidence.length > 0 && (
                            <div className="flex flex-col gap-2 mt-3">
                                {evidence.map(item => (
                                    <div key={item.id} className="flex items-center justify-between gap-2 bg-(--bg-grey) rounded-xl px-3 py-2 text-sm">
                                        <span className="truncate">{item.file.name}</span>
                                        <button type="button" onClick={() => removeFile(item.id)} className="cursor-pointer bg-transparent border-0 p-0 flex shrink-0">
                                            <img src={closeIcon} alt="remove" className="w-3 h-3"/>
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {error && <p className="text-(--text-red) mt-4 mb-0!">{error}</p>}

                    {/* Footer lives INSIDE the scrollable content for this modal (per request),
                        so it scrolls with the form instead of sticking to the bottom. */}
                    <div className="flex flex-col items-center gap-3 mt-8">
                        <button type="button" onClick={handleSubmit} disabled={isSubmitting || selectedIssues.size === 0}
                                className="hopn-add-passenger-button">
                            {isSubmitting ? 'Submitting...' : 'Submit Report'}
                        </button>
                        <button type="button" onClick={handleClose}
                                className="cursor-pointer bg-transparent border-0 text-[#FB5A2D] font-medium">
                            Cancel
                        </button>
                    </div>
                </div>
            )}
        </Modal>
    )
}
